<template>
  <v-card width="600">
    <v-skeleton-loader
      :loading="loading"
      elevation="2"
      type="card-heading, divider, list-item, image, list-item-three-line@3, divider, actions"
    >
      <v-card-title>
        {{ $t('ITEM.EDIT') }} {{ $t('SUBSCRIPTIONS.ITEM') }}

        <v-spacer />

        <v-chip
          v-if="subscription.status"
          class="mr-4"
          text-color="white"
          :color="subscription.status.color"
          small
        >
          {{ subscription.status.name }}
        </v-chip>

        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-card-subtitle v-if="errorsWithoutForm.length">
          <errors :errors="errorsWithoutForm" />
        </v-card-subtitle>

        <div class="d-flex justify-space-between">
          <p class="text-body-1">
            <span class="text--secondary">{{ $t('ITEM.DATE_ENTERED') }}:</span>
            <span class="text--primary">
              {{ subscription.created_at | dateFormat }}
            </span>
          </p>
          <p class="text-body-1 text-right">
            <span class="text--secondary">{{ $t('ITEM.UPDATED_AT') }}:</span>
            <span class="text--primary">
              {{ subscription.updated_at | dateFormat }}
            </span>
          </p>
        </div>

        <slot v-if="showDetails" name="details" :subscription="subscription">
          <subscription-details
            class="pa-4 mb-4"
            :subscription="subscription"
          />
        </slot>

        <v-form ref="form" v-model="valid" lazy-validation>
          <subscription-edit-form
            v-model="subscription"
            :action="$options.action"
            :person-id="subscription.person_id"
            :errors="errors.getAll()"
            @update-prop="clearErrorByName($event)"
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-btn outlined depressed color="grey" @click="cancel">
          {{ $t('BUTTON.CANCEL') }}
        </v-btn>

        <v-spacer />

        <v-btn depressed color="primary" :disabled="!valid" @click="update">
          {{ $t('BUTTON.UPDATE') }}
        </v-btn>
      </v-card-actions>
    </v-skeleton-loader>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { dateFormat } from '@/modules/_global/filters';
import { onceRequest } from '@/modules/_global/utils/common';
import errors from '@/modules/_global/mixins/errors';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import SubscriptionEditForm from '@/modules/subscriptions/components/forms/SubscriptionEditForm';
import { EDIT } from '@/modules/_global/mixins/form-editable.js';

export default {
  name: 'EditSubscription',

  action: EDIT,

  filters: {
    dateFormat
  },

  components: {
    Errors: () => import('@/modules/_global/_components/Errors'),
    SubscriptionEditForm,
    SubscriptionDetails: () => import('./SubscriptionDetail')
  },

  mixins: [errors, i18nRefreshData, errors],

  props: {
    id: {
      type: Number,
      required: true
    },
    showDetails: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      valid: true,
      loading: true,
      subscription: {
        include_in_rent: true
      }
    };
  },

  computed: {
    errorsWithoutForm() {
      return ['notruf_subscription']
        .reduce((acc, i) => acc.concat(this.errors.get(i)), [])
        .filter(i => i);
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('snackbar', ['setSnack']),
    ...mapActions('subscription', [
      'updateSubscription',
      'retrieveSubscription'
    ]),

    refreshData() {
      this.retrieveSubscription(this.id).then(({ data }) => {
        this.subscription = data;
        this.loading = false;
      });
    },

    cancel() {
      this.$emit('cancel');
    },

    update: onceRequest(function update() {
      if (!this.$refs.form.validate()) {
        return;
      }

      return this.updateSubscription({
        subscriptionId: this.id,
        subscription: this.subscription
      })
        .then(({ data }) => {
          this.setSnack({
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });

          this.$emit('updated', data);
        })
        .catch(this.handleErrors);
    })
  }
};
</script>
